import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, Plus, Calendar, Trash2, Edit2 } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { tasksApi, PRIORITY_LABELS, PRIORITY_COLORS } from './tasksApi';
import { useWorkAreas } from '../../contexts/WorkAreaContext';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const BACKLOG = '__backlog__';

const dateOnly = (s) => s ? new Date(s).toISOString().slice(0, 10) : '';

const taskInRange = (task, start, end) => {
  if (!start || !end || !task.task_local_date) return false;
  const d = task.task_local_date.slice(0, 10);
  return d >= start && d <= end;
};

export const SprintsView = ({ onOpenTask }) => {
  const { workAreaNames } = useWorkAreas();
  const [sprints, setSprints] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([tasksApi.listSprints(), tasksApi.listTasks()]);
      setSprints((s || []).sort((a, b) => {
        const aD = a.time_anchor?.date || '';
        const bD = b.time_anchor?.date || '';
        return aD.localeCompare(bD);
      }));
      setTasks(t || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const tasksBySprint = useMemo(() => {
    const map = { [BACKLOG]: [] };
    sprints.forEach((s) => { map[s.item_id] = []; });
    tasks.forEach((t) => {
      if (t.big_picture_id && map[t.big_picture_id]) {
        map[t.big_picture_id].push(t);
      } else {
        // Auto-suggest by date: if task delivery falls inside a sprint's range, group there
        const matching = sprints.find((s) =>
          taskInRange(t, s.time_anchor?.date, s.time_anchor?.end_date || s.time_anchor?.date)
        );
        if (matching && !t.big_picture_id) {
          map[matching.item_id].push({ ...t, _autoSuggested: true });
        } else {
          map[BACKLOG].push(t);
        }
      }
    });
    return map;
  }, [sprints, tasks]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const taskId = result.draggableId;
    const newSprintId = result.destination.droppableId;
    const task = tasks.find((t) => t.task_id === taskId);
    if (!task) return;
    const newBpId = newSprintId === BACKLOG ? null : newSprintId;
    if (task.big_picture_id === newBpId) return;
    setTasks((prev) => prev.map((t) => (t.task_id === taskId ? { ...t, big_picture_id: newBpId } : t)));
    try {
      await tasksApi.updateTask(taskId, { big_picture_id: newBpId });
      toast.success(newBpId ? 'Assigned to sprint' : 'Moved to backlog');
    } catch (e) {
      toast.error(e.message);
      load();
    }
  };

  const createSprint = async (body) => {
    setBusy(true);
    try {
      await tasksApi.createBpItem({
        type: 'SPRINT',
        title: body.title,
        sprint_goal: body.sprint_goal,
        date: body.start_date,
        end_date: body.end_date,
        time_anchor: { anchor_type: 'DATE', date: body.start_date, end_date: body.end_date, fuzziness: 'EXACT' },
        status: 'planned',
      });
      setShowCreate(false);
      toast.success('Sprint created — synced to Big Picture');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const updateSprint = async (id, body) => {
    setBusy(true);
    try {
      await tasksApi.updateBpItem(id, {
        title: body.title,
        sprint_goal: body.sprint_goal,
        time_anchor: { anchor_type: 'DATE', date: body.start_date, end_date: body.end_date, fuzziness: 'EXACT' },
      });
      setEditingSprint(null);
      toast.success('Sprint updated');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const deleteSprint = async (id) => {
    if (!window.confirm('Delete this sprint? Linked tasks will be unassigned.')) return;
    try {
      await tasksApi.deleteBpItem(id);
      toast.success('Sprint deleted');
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const sprintCapacity = (sprintId) => {
    const items = tasksBySprint[sprintId] || [];
    const totalEffort = items.reduce((sum, t) => sum + (t.task_estimated_effort || 0), 0);
    const done = items.filter((t) => t.task_status === 'Done').length;
    return { count: items.length, effort: totalEffort, done };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-xs">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> loading sprints
      </div>
    );
  }

  return (
    <div data-testid="sprints-view" className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Sprints</h2>
          <p className="text-[11px] text-muted-foreground">Linked to Big Picture · {sprints.length} sprint{sprints.length !== 1 ? 's' : ''}</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="sprint-new">
          <Plus className="w-3 h-3 mr-1" /> New Sprint
        </Button>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
          {/* Backlog column */}
          <SprintColumn
            droppableId={BACKLOG}
            title="Backlog"
            subtitle="Unassigned tasks"
            items={tasksBySprint[BACKLOG] || []}
            workAreaNames={workAreaNames}
            onOpenTask={onOpenTask}
            isBacklog
          />
          {sprints.map((sprint) => {
            const cap = sprintCapacity(sprint.item_id);
            const start = sprint.time_anchor?.date;
            const end = sprint.time_anchor?.end_date;
            return (
              <SprintColumn
                key={sprint.item_id}
                droppableId={sprint.item_id}
                title={sprint.title}
                subtitle={
                  <span>
                    {start && <span><Calendar className="w-3 h-3 inline mr-0.5" />{dateOnly(start)} → {dateOnly(end || start)}</span>}
                  </span>
                }
                items={tasksBySprint[sprint.item_id] || []}
                workAreaNames={workAreaNames}
                onOpenTask={onOpenTask}
                onEdit={() => setEditingSprint(sprint)}
                onDelete={() => deleteSprint(sprint.item_id)}
                meta={
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <Badge variant="outline" className="h-4 text-[9px]">{cap.count} tasks</Badge>
                    {cap.effort > 0 && <Badge variant="outline" className="h-4 text-[9px]">⚡{cap.effort}</Badge>}
                    {cap.done > 0 && <Badge className="h-4 text-[9px] bg-emerald-500">{cap.done} done</Badge>}
                  </div>
                }
                sprintGoal={sprint.sprint_goal}
              />
            );
          })}
        </div>
      </DragDropContext>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="sprint-create-dialog">
          <DialogHeader><DialogTitle>New Sprint</DialogTitle></DialogHeader>
          <SprintForm onSubmit={createSprint} disabled={busy} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingSprint} onOpenChange={(o) => !o && setEditingSprint(null)}>
        <DialogContent data-testid="sprint-edit-dialog">
          <DialogHeader><DialogTitle>Edit Sprint</DialogTitle></DialogHeader>
          {editingSprint && (
            <SprintForm
              initial={{
                title: editingSprint.title,
                sprint_goal: editingSprint.sprint_goal,
                start_date: editingSprint.time_anchor?.date || '',
                end_date: editingSprint.time_anchor?.end_date || '',
              }}
              onSubmit={(b) => updateSprint(editingSprint.item_id, b)}
              disabled={busy}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SprintColumn = ({ droppableId, title, subtitle, items, workAreaNames, onOpenTask, onEdit, onDelete, meta, sprintGoal, isBacklog }) => (
  <Droppable droppableId={droppableId}>
    {(provided, snapshot) => (
      <Card
        ref={provided.innerRef}
        {...provided.droppableProps}
        className={cn(
          'border-2 p-3 flex flex-col',
          snapshot.isDraggingOver ? 'border-violet-500 bg-violet-500/5' : 'border-border',
          isBacklog && 'bg-amber-50/50 dark:bg-amber-950/10'
        )}
        data-testid={`sprint-col-${droppableId}`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold truncate">{title}</h3>
            <div className="text-[10px] text-muted-foreground">{subtitle}</div>
          </div>
          {!isBacklog && (
            <div className="flex gap-0.5">
              {onEdit && <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={onEdit} data-testid={`sprint-edit-${droppableId}`}><Edit2 className="w-3 h-3" /></Button>}
              {onDelete && <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={onDelete} data-testid={`sprint-delete-${droppableId}`}><Trash2 className="w-3 h-3 text-destructive" /></Button>}
            </div>
          )}
        </div>
        {sprintGoal && <p className="text-[11px] text-muted-foreground italic mb-2">"{sprintGoal}"</p>}
        {meta && <div className="mb-2">{meta}</div>}
        <div className="space-y-1.5 flex-1 overflow-y-auto" style={{ minHeight: '60px', maxHeight: '320px' }}>
          {items.map((task, idx) => (
            <Draggable key={task.task_id} draggableId={task.task_id} index={idx}>
              {(prov, snap) => (
                <div
                  ref={prov.innerRef}
                  {...prov.draggableProps}
                  {...prov.dragHandleProps}
                  onClick={() => onOpenTask && onOpenTask(task)}
                  className={cn(
                    'bg-card border border-border rounded p-1.5 text-xs cursor-pointer hover:border-violet-500/50',
                    snap.isDragging && 'shadow-lg',
                    task._autoSuggested && 'border-dashed border-amber-500'
                  )}
                  data-testid={`sprint-card-${task.task_id}`}
                >
                  <div className="flex items-center gap-1 mb-0.5">
                    <Badge className={cn('text-[9px] h-3.5 px-1', PRIORITY_COLORS[task.task_priority ?? 3])}>
                      {PRIORITY_LABELS[task.task_priority ?? 3]}
                    </Badge>
                    {task._autoSuggested && <Badge variant="outline" className="text-[8px] h-3.5">auto by date</Badge>}
                  </div>
                  <div className="font-medium line-clamp-1">{task.task_title}</div>
                  {task.task_local_date && <div className="text-[9px] text-muted-foreground">{dateOnly(task.task_local_date)}</div>}
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
          {items.length === 0 && (
            <div className="text-center py-6 text-[11px] text-muted-foreground italic">
              {isBacklog ? 'No unassigned tasks' : 'Drag tasks here'}
            </div>
          )}
        </div>
      </Card>
    )}
  </Droppable>
);

const SprintForm = ({ initial = {}, onSubmit, disabled }) => {
  const [title, setTitle] = useState(initial.title || '');
  const [sprintGoal, setSprintGoal] = useState(initial.sprint_goal || '');
  const [startDate, setStartDate] = useState(initial.start_date || '');
  const [endDate, setEndDate] = useState(initial.end_date || '');
  return (
    <div className="space-y-3">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sprint name (e.g. Sprint 14)" data-testid="sprint-title-input" />
      <Input value={sprintGoal} onChange={(e) => setSprintGoal(e.target.value)} placeholder="Sprint goal (optional)" data-testid="sprint-goal-input" />
      <div className="grid grid-cols-2 gap-2">
        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="sprint-start-date" />
        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="sprint-end-date" />
      </div>
      <DialogFooter>
        <Button onClick={() => onSubmit({ title, sprint_goal: sprintGoal, start_date: startDate, end_date: endDate })}
          disabled={!title || !startDate || !endDate || disabled} data-testid="sprint-submit">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default SprintsView;
